import type { LiveSupportState } from '@/store/chat.store'

export const normalizeOperatorName = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null
  }

  const normalized = value.trim()

  if (!normalized || normalized.toLowerCase() === 'null') {
    return null
  }

  return normalized
}

export const getOperatorAvatarLabel = (operatorName?: string | null): string => {
  const normalized = normalizeOperatorName(operatorName)

  if (!normalized) {
    return 'OP'
  }

  const compactName = normalized.replace(/\s+/g, '')

  return compactName.slice(0, 2).toUpperCase() || 'OP'
}

export const normalizeQueuePosition = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10)

    if (Number.isFinite(parsed)) {
      return parsed
    }
  }

  return undefined
}

export const normalizeLiveSupportState = (value: unknown): LiveSupportState => {
  if (!value || typeof value !== 'object') {
    return null
  }

  const payload = value as Record<string, unknown>
  const operatorName = normalizeOperatorName(payload.operator_name ?? payload.operatorName)
  const queuePosition = normalizeQueuePosition(payload.queue_position ?? payload.queuePosition)
  const rawStatus = typeof payload.status === 'string' ? payload.status.trim().toLowerCase() : ''

  if (rawStatus === 'resolved') {
    return null
  }

  const inQueue =
    typeof payload.in_queue === 'boolean'
      ? payload.in_queue
      : typeof payload.isInQueue === 'boolean'
        ? payload.isInQueue
        : Boolean(rawStatus || operatorName || queuePosition !== undefined)

  if (!rawStatus && !operatorName && queuePosition === undefined && !inQueue) {
    return null
  }

  return {
    in_queue: inQueue,
    status: rawStatus || (operatorName ? 'processing' : 'pending'),
    ...(operatorName ? { operator_name: operatorName } : {}),
    ...(queuePosition !== undefined ? { queue_position: queuePosition } : {})
  }
}
